import { Row, Col } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { DataTableServerSide } from './DataTableServerSide'
import { useEffect, useState } from 'react'
import { getShareholdersDashboardCounts } from '../../core/_requests'
import { useAuth } from '../../../../auth'
import { MultipleStatisticsWidget } from '../../../founders-and-promoters/components/multiple-statistics-widget'
import Skeleton from 'react-loading-skeleton'
import { PieChartWidget } from '../../../../widgets/components/General/PieChartWidget'
import { WorldMap } from '../../../../widgets/components/General/WorldMap'
import { DisplayImage } from '../../../../widgets/components/General/DisplayImage'
import { parseCurrency } from '../../../../../core/_util'
import { ShareholdersTypes } from '../../core/_constants'
import NothingToShow from '../../../../captable-management/components/NothingToShow'

export default function Shareholder() {
  const { formatMessage } = useIntl()
  const { companyId } = useAuth()
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [dashboardCounts, setDashboardCounts] = useState<{
    shareholdersCount: {
      shareholdersIndividuals: {
        shareholdersIndividualsTotalCount: number
        shareholdersIndividualsCountByDay: any[]
      }
      shareholdersInstitutions: {
        shareholdersInstitutionsTotalCount: number
        shareholdersInstitutionsCountByDay: any[]
      }
      totalShareholdersCount: number
    }
    countriesCount: {
      indianCountryCount: number
      otherCountriesCount: {
        countryId: number
        countryName: string
      }[]
      totalOtherCountriesCount: number
      totalCountriesCount: number
    }
    stakeholderCategoryCount: {
      individuals: number
      institutions: number
      employees: number
    }
    topFiveShareholders:
    | {
      shareholderDetails: {
        shareholderId: number
        shareholderName: string
        entityType: string
        profileImage: string | null
      }
      totalShares: number
    }[]
    | []
  }>({
    shareholdersCount: {
      shareholdersIndividuals: {
        shareholdersIndividualsTotalCount: 0,
        shareholdersIndividualsCountByDay: [],
      },
      shareholdersInstitutions: {
        shareholdersInstitutionsTotalCount: 0,
        shareholdersInstitutionsCountByDay: [],
      },

      totalShareholdersCount: 0,
    },

    countriesCount: {
      indianCountryCount: 0,
      otherCountriesCount: [
        {
          countryId: 0,
          countryName: '',
        },
      ],
      totalOtherCountriesCount: 0,
      totalCountriesCount: 0,
    },
    stakeholderCategoryCount: {
      individuals: 0,
      institutions: 0,
      employees: 0,
    },
    topFiveShareholders: [],
  })

  const getDashboardCounts = async () => {
    setDashboardLoading(true)
    try {
      const {
        data: { data },
      } = await getShareholdersDashboardCounts(companyId!)
      if (data) {
        setDashboardCounts(data)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setDashboardLoading(false)
    }
  }

  useEffect(() => {
    getDashboardCounts()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {dashboardLoading ? (
        <Row className={`my-4`}>
          {[1, 2, 3, 4].map((item) => (
            <Col md={6} lg={3} key={item}>
              <div
                className='bg-white   rounded-2 mb-4 p-6'
                style={{ width: '100%', height: '270px' }}
              >
                <div className='mb-2'>
                  <Skeleton
                    width={'100%'}
                    height={20}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                </div>

                <div className='d-flex justify-content-between mb-2'>
                  <div>
                    <Skeleton
                      width={'5vw'}
                      height={40}
                      baseColor='var(--kt-skeleton-bg-color)'
                      highlightColor='var(--kt-skeleton-highlight-bg-color)'
                    />
                  </div>
                  <div>
                    <Skeleton
                      width={'5vw'}
                      height={40}
                      baseColor='var(--kt-skeleton-bg-color)'
                      highlightColor='var(--kt-skeleton-highlight-bg-color)'
                    />{' '}
                  </div>
                </div>

                <div className='mb-2'>
                  <Skeleton
                    width={'100%'}
                    height={150}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <Row className={`mt-4`}>
          <Col md={6} lg={3}>
            <div className='bg-white rounded-2 mb-4' style={{ height: '300px' }}>
              {dashboardCounts?.shareholdersCount?.totalShareholdersCount === 0 ?
                <>
                  <div className='stats-card-top p-6'>
                    <p className='mb-0 text-muted mb-4'>{formatMessage({ id: 'Stakeholder Counts' })}</p>
                    <NothingToShow />
                  </div>
                </>
                :
                <>
                  <div className='stats-card-top p-6'>
                    <p className='mb-0 text-muted mb-4'>{formatMessage({ id: 'Stakeholder Counts' })}</p>
                    <div className='d-flex flex-row align-items-center justify-content-between mt-6'>
                      <h1 className='mb-0 dark_text_color font-size-32'>
                        {dashboardCounts?.shareholdersCount?.totalShareholdersCount}
                      </h1>
                      <div className='w-50'>
                        <div className='d-flex justify-content-between font-size-12 mb-1'>
                          <span>
                            <span className='light-blue-circle me-1'></span>{formatMessage({ id: 'Individuals' })}
                          </span>
                          <span>
                            {
                              dashboardCounts.shareholdersCount.shareholdersIndividuals
                                .shareholdersIndividualsTotalCount
                            }
                          </span>
                        </div>
                        <div className='d-flex justify-content-between font-size-12 mb-1'>
                          <span>
                            <span className='strong-blue-circle me-1'></span>{formatMessage({ id: 'Institutions' })}
                          </span>
                          <span>
                            {
                              dashboardCounts.shareholdersCount.shareholdersInstitutions
                                .shareholdersInstitutionsTotalCount
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <MultipleStatisticsWidget
                      className='card-xl-stretch mb-xl-1'
                      color={['blue', '#062EC6']}
                      statisticsData={[
                        dashboardCounts?.shareholdersCount?.shareholdersIndividuals
                          .shareholdersIndividualsCountByDay as any,
                        dashboardCounts?.shareholdersCount?.shareholdersInstitutions
                          ?.shareholdersInstitutionsTotalCount as any,
                      ]}
                      label={['individuals', 'institutions']}
                    />
                  </div>
                </>
              }
            </div>
          </Col>

          <Col md={6} lg={3}>
            <div className='bg-white p-4 pt-6 rounded-2 mb-4' style={{ height: '300px' }}>
              {!dashboardCounts.stakeholderCategoryCount.individuals && !dashboardCounts.stakeholderCategoryCount.institutions && !dashboardCounts.stakeholderCategoryCount.employees ?
                <>
                  <div className='stats-card-top'>
                    <p className='mb-0 text-muted mb-4'>
                      {formatMessage({ id: 'Stakeholders By Type' })}
                    </p>
                    <NothingToShow />
                  </div>
                </>
                :
                <>
                  <div className='stats-card-top'>
                    <p className='mb-0 text-muted mb-4'>
                      {formatMessage({ id: 'Stakeholders By Type' })}
                    </p>
                  </div>
                  <div>
                    <PieChartWidget
                      data={[
                        {
                          name: 'Individuals',
                          value: dashboardCounts.stakeholderCategoryCount.individuals,
                        },
                        {
                          name: 'Institutions',
                          value: dashboardCounts.stakeholderCategoryCount.institutions,
                        },
                        { name: 'Employees', value: dashboardCounts.stakeholderCategoryCount.employees },
                      ]}
                      colors={['#4776E6', '#0D216D', '#1D3EB9']}
                    />
                  </div>
                </>
              }
            </div>
          </Col>

          <Col md={6} lg={3}>
            <div className='bg-white p-6 pb-0 rounded-2 mb-4' style={{ height: '300px' }}>
              <div className='stats-card-top'>
                <p className='mb-0 text-muted mb-4'>
                  {formatMessage({ id: 'Stakeholder By Country' })}
                </p>
                <div className='d-flex flex-row align-items-center justify-content-between mt-3'>
                  <h1 className='mb-0 dark_text_color font-size-32'>
                    {dashboardCounts.countriesCount.totalCountriesCount}
                  </h1>
                  <div className='w-50'>
                    <div className='d-flex justify-content-between font-size-12 mb-1'>
                      <span>
                        <span className='light-blue-circle me-1'></span>{formatMessage({ id: 'Individuals' })}
                      </span>
                      <span>{dashboardCounts.countriesCount.indianCountryCount}</span>
                    </div>
                    <div className='d-flex justify-content-between font-size-12 mb-1'>
                      <span>
                        <span className='strong-blue-circle me-1'></span>{formatMessage({ id: 'Institutions' })}
                      </span>
                      <span>{dashboardCounts.countriesCount.totalOtherCountriesCount}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <WorldMap dashboardCounts={dashboardCounts} />
              </div>
            </div>
          </Col>

          <Col md={6} lg={3}>
            <div
              className='bg-white p-6 rounded-2 mb-4'
              style={{ height: '300px' }}
            >
              {!dashboardCounts.topFiveShareholders ?
                <>
                  <div className='stats-card-top'>
                    <p className='mb-0 text-muted mb-4'>
                      {formatMessage({
                        id: `Top #5 Shareholders`,
                      })}
                    </p>
                    <NothingToShow />
                  </div>
                </>
                :
                <div className='stats-card-top'>
                  <p className='mb-0 text-muted mb-4'>
                    {formatMessage({
                      id: `Top #${dashboardCounts.topFiveShareholders.length ? dashboardCounts.topFiveShareholders.length : 5} Shareholders`,
                    })}
                  </p>
                  {dashboardCounts.topFiveShareholders.map((shareholder, index) => (
                    <StakeholdersListItem
                      key={shareholder.shareholderDetails.shareholderId}
                      name={shareholder.shareholderDetails.shareholderName}
                      entityType={shareholder.shareholderDetails.entityType}
                      totalShares={dashboardCounts.topFiveShareholders[index].totalShares}
                      imgName={shareholder.shareholderDetails.profileImage}
                    />
                  ))}
                </div>
              }
            </div>
          </Col>
        </Row>
      )}

      <DataTableServerSide />
    </>
  )
}

function StakeholdersListItem({
  entityType,
  imgName,
  name,
  totalShares,
}: {
  entityType: string
  imgName: any
  name: string
  totalShares: number
}) {

  return (
    <>
      <div className='d-flex flex-row align-items-center mt-5'>
        <div className='d-flex w-100 gap-2'>
          <span>
            <DisplayImage
              imgName={imgName ? imgName?.name : 'dummy'}
              height='24px'
              width='24px'
              fit='contain'
              alt='profile'
              className='image-rounded'
            />
          </span>
          <div className='d-flex align-items-center justify-content-between w-100'>
            <h1 className='m-0 font-size-13 fw-semibold'>{name}</h1>
            <div
              className={`p-2 badge font-size-12 ${entityType === ShareholdersTypes.INDIVIDUAL
                ? 'badge-light-primary'
                : 'badge-light-success'
                }`}
            >
              {parseCurrency(totalShares)}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
